import { useEffect, useState } from 'react'
import { useUserContext } from '@vp/ubik-context'
import { buildDefaultViewImageUrl, fetchAllViews } from '@shared/api/purcs.service'
import { logger } from '@shared/lib/logger'
import { attempt } from '@shared/utils'
import type { WorkEntity } from '@shared/types/work-entity.types'

interface ProjectImageResult {
  src: string | undefined
  srcSet: string | undefined
}

export const useWorkEntityImage = (workEntity: WorkEntity): ProjectImageResult => {
  const { locale } = useUserContext()
  const previewInstructionsUri = workEntity.design?.displayUrl
  const productKey = workEntity.product?.key

  const [src, setSrc] = useState<string | undefined>(undefined)
  const [srcSet, setSrcSet] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!previewInstructionsUri || !productKey) {
      setSrc(undefined)
      setSrcSet(undefined)
      return
    }

    let cancelled = false
    const productVersion = Number(workEntity.product?.version)
    const optionSelections = workEntity.merchandising?.merchandisingSelections ?? {}

    const fallbackImage = buildDefaultViewImageUrl({
      culture: locale,
      productKey,
      productVersion: Number.isFinite(productVersion) ? productVersion : undefined,
      optionSelections,
      previewInstructionsUri,
    })

    setSrc(fallbackImage.src)
    setSrcSet(fallbackImage.srcSet)

    const loadBestView = async (): Promise<void> => {
      const { data: views, error } = await attempt(
        fetchAllViews({
          culture: locale,
          productKey,
          productVersion: Number.isFinite(productVersion) ? productVersion : undefined,
          optionSelections,
          previewInstructionsUri,
        }),
      )

      if (error) {
        logger.error('Contact Us Work Entity Image', {
          action: 'load_best_view',
          errorMessage: `Failed to load views for product ${productKey}: ${error.message}`,
        })
        return
      }

      if (cancelled) return

      if (views.length > 0 && views[0]) {
        setSrc(views[0].src)
        setSrcSet(views[0].srcSet)
      }
    }

    void loadBestView()

    return () => {
      cancelled = true
    }
  }, [locale, workEntity.workRevisionId, previewInstructionsUri, productKey])

  return { src, srcSet }
}
